import Modal from '../modal/modal'
import { bodyElement } from '../common/body-element'
import { type Nullable } from '../../types/common'
import { PopupStorage } from './popup-storage'
import { PopupType, type PopupVariant } from './types'
import { ConditionRuntime } from './condition-runtime'
import { gradientOpacify } from './utils'
import { POPUP_CLASSNAMES } from './constants'

export class PopupPromo extends Modal {
  storage: PopupStorage
  conditionRuntime?: ConditionRuntime
  variant: PopupVariant

  constructor() {
    super()
    this.storage = new PopupStorage(this.dataset.blockId ?? this.id)
    this.variant = this.dataset.variant as PopupVariant
  }

  connectedCallback(): void {
    if (this.isEditorMode) {
      this.editor.subscribe('BLOCK_SELECT', this._handleBlockSelect)
      this.editor.subscribe('BLOCK_DESELECT', this._handleBlockDeselect)
      return
    }

    const data = this.storage.loadData()

    if (!data || data.type === PopupType.Promo) {
      if (data?.lastShow) {
        try {
          data.lastShow = new Date(data.lastShow)
        } catch {
          data.lastShow = null
        }
      }

      this.conditionRuntime = new ConditionRuntime(
        this.dataset,
        data?.lastShow ?? null,
        this.show,
      )
      this.conditionRuntime.run()
    }
  }

  disconnectedCallback(): void {
    if (this.isEditorMode) {
      this.editor.destroy()
    }
    this.conditionRuntime?.cleanup()
  }

  _handleBlockSelect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.show()
    }
  }

  _handleBlockDeselect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.hide()
    }
  }

  show = async (): Promise<void> => {
    if (this.variant === 'lightbox') {
      const withGradient = !!this.dataset.overlayGradient

      const background = withGradient
        ? gradientOpacify(
            this.dataset.overlayGradient,
            this.dataset.overlayOpacity,
          )
        : `color-mix(in srgb, ${this.dataset.overlaySolid} ${
            this.dataset.overlayOpacity * 100
          }%, transparent)`

      bodyElement.showOverlay(this.id, {
        zIndex: 15,
        background,
        backdropFilter: `blur(${this.dataset.overlayBlur}px)`,
        transitionDuration: '750ms',
      })
    }

    await super.show()

    if (this.variant === 'lightbox') {
      this.classList.add(POPUP_CLASSNAMES.active)
    }

    this.selectBtn('close')?.addEventListener('click', this.hide)
    this.selectBtn('close-small')?.addEventListener('click', this.hide)
    this.selectBtn('promo')?.addEventListener('click', this.onTouch)
  }

  hide = async (): Promise<void> => {
    this.onTouch()

    if (this.variant === 'lightbox') {
      bodyElement.hideOverlay(this.id)
    }

    this.conditionRuntime?.cleanup()

    this.selectBtn('close')?.removeEventListener('click', this.hide)
    this.selectBtn('close-small')?.removeEventListener('click', this.hide)
    this.selectBtn('promo')?.removeEventListener('click', this.onTouch)

    if (this.variant === 'lightbox') {
      this.classList.remove(POPUP_CLASSNAMES.active)
      this.classList.add(POPUP_CLASSNAMES.closing)
    }

    await super.hide()
    this.classList.remove(POPUP_CLASSNAMES.closing)
  }

  onTouch = (): void => {
    this.storage.saveData({
      type: PopupType.Promo,
      lastShow: new Date(),
    })
  }

  selectBtn(type: 'promo' | 'close' | 'close-small'): Nullable<HTMLElement> {
    if (!this.isOpen) {
      return null
    }

    return this.querySelector(`[data-${type}-btn]`)
  }
}
